import React, { useCallback, useMemo } from 'react';
import { ColumnsType } from 'antd/es/table';
import { ExpandableConfig } from 'antd/es/table/interface';
import { Column, DataTable } from '@ydistri/ds';
import { CustomExportRunItemResponse, CustomExportRunItemStatus } from '@ydistri/api-sdk';
import { formatDateTime } from '@ydistri/generic-utils';
import { useTemplateOrCalculation } from '../../../../hooks/useTemplateOrCalculation';
import LoadingIndicator from '../../../../components/global/LoadingIndicator/LoadingIndicator';
import { useGetCustomExportRunItemsQuery } from '../apiCalculationExport';
import { defaultCustomExportListQuery, sortCustomExportByCreatedAtAsc } from '../customExportLib';
import CustomExportRunDoneWithFailedAction from './CustomExportRunDoneWithFailedAction';
import CustomExportRunItemDetail from './CustomExportRunItemDetail';
import CustomExportStatusBadge from './CustomExportStatusBadge';

interface CustomExportRunItemTableProps {
  customExportRunId: number;
  latest: boolean;
}

const CustomExportRunItemTable: React.FC<CustomExportRunItemTableProps> = ({
  customExportRunId,
  latest,
}) => {
  const templateOrCalculation = useTemplateOrCalculation();
  const { data, isFetching, isError } = useGetCustomExportRunItemsQuery({
    calculationId: templateOrCalculation.id,
    customExportRunId,
    query: defaultCustomExportListQuery,
  });

  const items = useMemo(() => sortCustomExportByCreatedAtAsc(data?.data ?? []), [data?.data]);

  const columns = useMemo<ColumnsType<CustomExportRunItemResponse>>(
    () => [
      {
        title: 'Item ID',
        dataIndex: 'id',
        key: 'id',
        width: '8rem',
      },
      {
        title: 'Run ID',
        dataIndex: 'customExportRunId',
        key: 'customExportRunId',
        width: '7rem',
      },
      {
        title: 'Status',
        dataIndex: 'status',
        key: 'status',
        width: '10rem',
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- status type
        render: value => <CustomExportStatusBadge status={value as CustomExportRunItemStatus} />,
      },
      {
        title: 'File download',
        dataIndex: 'fileForDownloadUrl',
        key: 'fileForDownloadUrl',
        width: '9rem',
        render: (value?: string | null) =>
          value ? (
            <a href={value} target="_blank" rel="noreferrer">
              Open
            </a>
          ) : (
            'N/A'
          ),
      },
      {
        title: 'Original item',
        dataIndex: 'originalCustomExportRunItemId',
        key: 'originalCustomExportRunItemId',
        width: '11rem',
        render: (value?: number | null) => value ?? 'N/A',
      },
      {
        title: 'Created at',
        dataIndex: 'createdAt',
        key: 'createdAt',
        width: '12rem',
        render: (value: string) => formatDateTime(value),
      },
    ],
    [],
  );

  const expandableConfig = useMemo<ExpandableConfig<CustomExportRunItemResponse>>(
    () => ({
      expandedRowRender: record => (
        <CustomExportRunItemDetail
          customExportRunId={customExportRunId}
          customExportRunItemId={record.id}
        />
      ),
    }),
    [customExportRunId],
  );

  const rowKeyProvider = useCallback((record: CustomExportRunItemResponse) => record.id, []);

  if (isFetching && !data) {
    return (
      <Column $padding={1}>
        <LoadingIndicator />
      </Column>
    );
  }

  if (isError) {
    return (
      <Column $padding={1}>
        <div>Failed to load custom export run items.</div>
      </Column>
    );
  }

  if (items.length === 0) {
    return (
      <Column $padding={1}>
        <div>No custom export run items found.</div>
      </Column>
    );
  }

  return (
    <Column $padding={1} $gap="0.75rem">
      {latest && (
        <CustomExportRunDoneWithFailedAction customExportRunId={customExportRunId} items={items} />
      )}
      <DataTable<CustomExportRunItemResponse>
        id={`custom-export-run-items-${customExportRunId}`}
        dataSource={items}
        columns={columns}
        rowKey={rowKeyProvider}
        loading={isFetching}
        showHeader={true}
        showBorder={true}
        expandable={expandableConfig}
      />
    </Column>
  );
};

export default CustomExportRunItemTable;
